import { formatCurrency } from '@/helpers'
import { useEffect, useState } from 'react'
import { DECIMAL_PLACES_DEFAULT } from '@/constants'

import { InputCurrencyProps } from './input-currency.type'
import styles from './input-currency.module.css'
import { generateClasses } from './input-currency.constants'
import { InputNumber } from '../../core'
import { Text } from '../../core/text'

import { SwapVertIcon } from '../../../icons'

export const InputCurrency = ({
  value = '',
  onChange,
  primaryCurrency = 'USD',
  secondaryCurrency = 'MXN',
  exchangeRate,
  title = '',
  disabled = false,
  className = '',
  primaryPlaceholder = '',
  secondaryPlaceholder = '',
  decimalPrecision = DECIMAL_PLACES_DEFAULT,
  size = 'medium',
  showHeader = true,
  error,
  required = false,
}: InputCurrencyProps) => {
  const [primaryInputValue, setPrimaryInputValue] = useState<string | number>()
  const [secondaryInputValue, setSecondaryInputValue] = useState<
    string | number
  >('')

  const setSecondaryValue = (value: string | number) => {
    let formattedValue: string | number = ''
    if (primaryCurrency === 'USD') {
      formattedValue = Number(value) * exchangeRate
    } else {
      formattedValue = Number(value) / exchangeRate
    }

    setSecondaryInputValue(formattedValue)
  }

  useEffect(() => {
    setPrimaryInputValue(value)
    setSecondaryValue(value)
  }, [value])

  const handlePrimaryChange = (value: string | number) => {
    setPrimaryInputValue(value)

    if (value && !isNaN(Number(value))) {
      const numValue = Number(value)
      const convertedValue =
        primaryCurrency === 'USD'
          ? numValue * exchangeRate
          : numValue / exchangeRate

      setSecondaryInputValue(convertedValue)
    } else {
      setSecondaryInputValue('')
    }

    onChange?.(value)
  }

  const handleSecondaryChange = (value: string | number) => {
    setSecondaryInputValue(value)

    if (value && !isNaN(Number(value))) {
      const numValue = Number(value)
      const convertedValue =
        secondaryCurrency === 'MXN'
          ? numValue / exchangeRate
          : numValue * exchangeRate

      setPrimaryInputValue(convertedValue)
      onChange?.(convertedValue)
    } else {
      setPrimaryInputValue('')
      onChange?.('')
    }
  }

  const formatExchangeRate = () => {
    return `1.000 USD = ${formatCurrency(exchangeRate, 'es-MX', 'MXN', DECIMAL_PLACES_DEFAULT)}`
  }

  return (
    <div>
      <div
        className={`${generateClasses(size, !!error, disabled)} ${className}`}
      >
        {showHeader && (
          <div className={styles.header}>
            <Text variant="title-medium" className={styles.title}>
              {title} {required && <span className={styles.required}>*</span>}
            </Text>
            <Text variant="title-base" className={styles['exchange-rate']}>
              {formatExchangeRate()}
            </Text>
          </div>
        )}

        <div className={styles['input-group']}>
          <Text variant="title-medium" className={styles['currency-label']}>
            {primaryCurrency}
          </Text>
          <InputNumber
            value={primaryInputValue}
            onChange={handlePrimaryChange}
            placeholder={primaryPlaceholder}
            disabled={disabled}
            decimalPrecision={decimalPrecision}
            className={styles.input}
            size={size}
          />
        </div>

        <div className={styles.divider}>
          <p></p>
          <div className={styles['swap-icon']}>
            <SwapVertIcon />
          </div>
          <p></p>
        </div>

        <div className={styles['input-group']}>
          <Text variant="title-medium" className={styles['currency-label']}>
            {secondaryCurrency}
          </Text>
          <InputNumber
            value={secondaryInputValue}
            onChange={handleSecondaryChange}
            placeholder={secondaryPlaceholder}
            disabled={disabled}
            className={styles.input}
            decimalPrecision={decimalPrecision}
            size={size}
          />
        </div>
      </div>
      {!error || <Text className={styles.error}>{error}</Text>}
    </div>
  )
}
